import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { AIChatIcon } from './CustomIcons';
import { apiService } from '../services/api';
import '../styles/chat-side-panel.css';

interface ChatHistoryIndicatorProps {
  problemId: string;
  problemTitle: string;
  onChatOpen: () => void;
}

export const ChatHistoryIndicator: React.FC<ChatHistoryIndicatorProps> = ({
  problemId,
  problemTitle,
  onChatOpen,
}) => {
  const { isAuthenticated } = useAuth();
  const [hasHistory, setHasHistory] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isDiscovered, setIsDiscovered] = useState(false);

  // Check if AI assistant has been discovered this session
  useEffect(() => {
    const discovered = sessionStorage.getItem('aiAssistantDiscovered') === 'true';
    setIsDiscovered(discovered);
  }, []);

  // Check for chat history when component mounts (only for authenticated users)
  useEffect(() => {
    // Skip history check for unauthenticated users - they can't have history
    if (!isAuthenticated || !problemId) return;

    const checkHistory = async () => {
      try {
        setIsChecking(true);
        const response = await apiService.getChatHistory(problemId);
        if (response.success && response.data?.messages?.length > 0) {
          setHasHistory(true);
        }
      } catch (error) {
        // Silently fail - just don't show the indicator
        console.debug('No chat history found:', error);
      } finally {
        setIsChecking(false);
      }
    };

    // Debounce the check to avoid excessive API calls
    const timeoutId = setTimeout(checkHistory, 500);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, problemId]);

  // Show chat icon for all users - auth/credit check happens on click

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark AI assistant as discovered for this session
    if (!isDiscovered) {
      sessionStorage.setItem('aiAssistantDiscovered', 'true');
      setIsDiscovered(true);
    }

    onChatOpen();
  };

  // Only show history indicator (blue dot) for authenticated users with history
  const showHistoryIndicator = isAuthenticated && hasHistory;

  return (
    <button
      onClick={handleClick}
      className={`github-ai-assistant-btn ${showHistoryIndicator ? 'has-history' : ''} ${isDiscovered ? 'discovered' : ''}`}
      title={`AI Assistant for "${problemTitle}"${showHistoryIndicator ? ' (Has chat history)' : ''}`}
      aria-label={`AI Assistant for "${problemTitle}"${showHistoryIndicator ? ' (Has chat history)' : ''}`}
      disabled={isChecking}
    >
      <AIChatIcon size={20} color="white" className="ai-icon" />
      {showHistoryIndicator && <span className="history-indicator" aria-label="Has chat history" />}
    </button>
  );
};